import {
  type IntegrationActivity,
  type MediaPartnerKey,
} from '@/api/routes/partners/partners.integration.api';
import { type IntegrationKeyState } from '@/domain/integration/types';
import { getActiveLocale } from '@/i18n/activeLocale';
import { SUPPORT_PHONE } from '@/utils/data';

/** Bodyprint support on WhatsApp: keys are swapped or revoked by the team. */
export const INTEGRATION_SUPPORT_URL = `https://wa.me/${SUPPORT_PHONE}`;

/** The key the page shows: the active one, else the most recent revoked one. */
export const getDisplayedKey = (keys: MediaPartnerKey[]): MediaPartnerKey | undefined =>
  keys.find((key) => !key.revokedAt) ??
  [...keys].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

export const getKeyState = (key: Pick<MediaPartnerKey, 'revokedAt'>): IntegrationKeyState =>
  key.revokedAt ? 'revoked' : 'active';

/** "bpm_••••••••0001": only the last four characters ever reach the portal. */
export const maskKey = (last4: string): string => `bpm_••••••••${last4}`;

export const totalClicks = (activity: IntegrationActivity): number =>
  activity.galleryClicks + activity.mediaClicks;

/** "12 out 2026, 09:26". */
export const formatIntegrationDate = (value: string): string =>
  new Intl.DateTimeFormat(getActiveLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(value))
    .replace('.', '')
    .replace(/ de /g, ' ');

/** "1.284". */
export const formatIntegrationCount = (value: number): string =>
  new Intl.NumberFormat(getActiveLocale()).format(value);

/** Examples of the contract (bodyprint-api/docs/external-apis/media-partners.md), shown as code. */
export const SUBJECTS_EXAMPLE = `GET /media-partners/v1/subjects?cursor={nextCursor}&limit=100
Authorization: Bearer <sua chave>

{
  "items": [
    {
      "subjectId": "sbj_4f…",
      "status": "active",
      "updatedAt": "2026-10-10T12:00:00.000Z",
      "name": "Luana Nascimento",
      "email": "luana@…",
      "city": "São Paulo",
      "state": "SP",
      "photoUrl": "https://…",
      "recentSessions": [
        {
          "startedAt": "2026-10-09T09:05:00.000Z",
          "endedAt": "2026-10-09T09:55:00.000Z",
          "sport": "run",
          "startAddress": "Av. Pedro Álvares Cabral, 1500 - Vila Mariana, São Paulo - SP"
        }
      ]
    },
    { "subjectId": "sbj_9a…", "status": "revoked", "updatedAt": "…" }
  ],
  "nextCursor": "MjAyNi0xMC0xMFQx…"
}`;

export const MEDIA_EXAMPLE = `POST /media-partners/v1/media
Authorization: Bearer <sua chave>

{
  "subjectId": "sbj_4f…",
  "galleryUrl": "https://…/minhas-fotos",
  "items": [
    {
      "externalId": "FR-77120",
      "takenAt": "2026-10-09T06:41:00-03:00",
      "placeLabel": "Parque Ibirapuera",
      "eventName": null,
      "previewUrl": "https://…/preview/77120.jpg",
      "purchaseUrl": "https://…/foto/77120"
    }
  ]
}

→ 200 { "received": 1, "created": 1 }`;
